import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { SyntheticEvent } from 'react';

import type { LinkingOptions } from '@atlaskit/editor-common/types/hyperlink';
import type { CardContext } from '@atlaskit/link-provider/types';
import { useSmartLinkContext } from '@atlaskit/link-provider/context';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';

import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import { expVal } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { smartCardEventHandler } from '@confluence/linking-platform/entry-points/smartCardEventHandler.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

// eslint-disable-next-line import/no-cycle
import { useLinkingOptions } from '../hooks/useLinkingOptions.tsx';

import { RovoLinkPickerTabPreferenceProvider } from './RovoLinkPickerTabPreferenceContext.tsx';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

/**
 * Lazily loaded adapter that maps the editor's LinkPickerProps interface to RovoLinkPicker.
 * Loaded only when the `confluence_rovo_link_picker_experiment` experiment is enabled (TREX-1074),
 * keeping @atlassian/rovo-link-picker and its heavy transitive deps out of the baseline editor bundle.
 */
const RovoLinkPickerAdapter = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RovoLinkPickerAdapter" */ './RovoLinkPickerAdapter.tsx'
			)
		).RovoLinkPickerAdapter,
});

type AkEditorLinkingProp = {
	smartCardContext?: CardContext;
	linking: LinkingOptions;
};

type CardOptions = {
	allowEmbeds: boolean;
	allowBlockCards: boolean;
	allowDatasource: boolean;
};
interface EditorLinkingProps {
	children: (props: AkEditorLinkingProp) => React.ReactElement<any, any> | null;
	options?: Partial<CardOptions>;
	viewMode?: boolean;
	isChromelessEditor?: boolean;
	isLivePage?: boolean;
}

const useUndefined = () => undefined;

export const useLinkingFeatures = (
	options?: Partial<CardOptions>,
	viewMode?: boolean,
	isChromelessEditor?: boolean,
	isLivePage?: boolean,
): AkEditorLinkingProp => {
	const config = useLinkingOptions(options, isChromelessEditor);
	const { cloudId } = useSessionData();
	// TODO MODES-4363 Once getSpaTransitionDetails & its consumers are refactored to not use RoutesContext, use RouteState instead of RoutesContext
	const routesContext = useContext(RoutesContext);

	const useSmartLinkContextSafe = conditionalHooksFactory(
		() => isSSRStreaming(),
		useSmartLinkContext,
		useUndefined,
	);
	const smartCardContext = useSmartLinkContextSafe();

	const productConfig = useMemo(
		() => ({
			product: 'confluence',
			cloudId,
		}),
		[cloudId],
	);

	/**
	 * Store live page state props in refs so the onClickCallback has a stable reference
	 * The onClickCallback passed to editor (and consumed within the useUniversalPreset hook)
	 * is not updated when the live page state changes.
	 */
	const isLivePageRef = useRef(isLivePage);
	const viewModeRef = useRef(viewMode);

	useEffect(() => {
		isLivePageRef.current = isLivePage;
		viewModeRef.current = viewMode;
	}, [isLivePage, viewMode]);

	/**
	 * By passing a callback we circumvent the default link click handling within platform
	 *
	 * We only want to allow smart links in live edit mode to navigte when this platform FF
	 * is enabled. The guarded behaviour can be instilled as the default on clean up (alongside
	 * removing the viewMode prop).
	 */

	const onClickCallback = useCallback(
		({ event, url }: { event: SyntheticEvent<HTMLAnchorElement>; url?: string }) => {
			if (viewModeRef.current && url) {
				smartCardEventHandler(routesContext, event, url);
			}
		},
		[routesContext],
	);

	// Always called (rules of hooks); result discarded in treatment — useLinkPickerEditorProps
	// replaces `component` with its own wrapper, which RovoLinkPicker doesn't need.
	const defaultLinkPickerProps = useLinkPickerEditorProps(
		undefined,
		expVal('confluence_rovo_link_picker_experiment', 'isEnabled', false)
			? undefined
			: productConfig,
	);

	return useMemo<AkEditorLinkingProp>(
		() => ({
			smartCardContext,
			linking: {
				...config.linking,
				linkPicker: (expVal('confluence_rovo_link_picker_experiment', 'isEnabled', false)
					? {
							...config.linking?.linkPicker,
							component: RovoLinkPickerAdapter,
						}
					: {
							...config.linking?.linkPicker,
							...defaultLinkPickerProps,
						}) as typeof config.linking.linkPicker,
				smartLinks: {
					...config.linking?.smartLinks,
					onClickCallback,
				},
			},
		}),
		// expVal is stable per session (experiment assignment doesn't change during a render lifecycle),
		// so it's intentionally omitted from dependencies
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[config, defaultLinkPickerProps, onClickCallback, smartCardContext],
	);
};

/**
 * Component that provides linking features to editor via props
 */
export const EditorLinkingFeatures = ({
	children,
	options,
	viewMode = false,
	isChromelessEditor = false,
	isLivePage = false,
}: EditorLinkingProps): React.JSX.Element | null => {
	const mergedProps = useLinkingFeatures(options, viewMode, isChromelessEditor, isLivePage);

	if (expVal('confluence_quick_insert_embeds', 'cohort', 'control') !== 'control') {
		return (
			<RovoLinkPickerTabPreferenceProvider>
				{children(mergedProps)}
			</RovoLinkPickerTabPreferenceProvider>
		);
	}

	return children(mergedProps);
};
